import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { LogIn, KeyRound, User as UserIcon, Loader2, ShieldCheck, ArrowRight } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { SCHOOL_LOGO, SCHOOL_NAME } from "../lib/constants";
import { toast } from "sonner";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/students";

  // If already authenticated, skip login and go to student dashboard
  useEffect(() => {
    if (user) nav("/students", { replace: true });
  }, [user, nav]);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("الرجاء إدخال اسم المستخدم وكلمة المرور");
      return;
    }
    setLoading(true);
    try {
      await login(username.trim(), password);
      toast.success("مرحباً بك");
      nav(from, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "تعذر تسجيل الدخول");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-grid-teal bg-[#F6FAF9] p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-teal-500/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-amber-400/10 blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        <Link
          to="/"
          data-testid="login-back-portal"
          className="inline-flex items-center gap-1.5 text-slate-600 hover:text-teal-700 text-xs font-cairo mb-3 transition-colors"
        >
          <ArrowRight size={14} className="rotate-180" /> العودة إلى بوابة الأنظمة
        </Link>
        <div className="bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="relative px-7 py-7 bg-gradient-to-l from-teal-700 to-teal-800 text-white">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-white p-1.5 shadow-md shrink-0">
                <img src={SCHOOL_LOGO} alt="logo" className="w-full h-full object-contain" />
              </div>
              <div>
                <div className="text-[11px] tracking-[0.2em] text-teal-100 font-cairo">المملكة العربية السعودية</div>
                <div className="font-cairo text-lg font-extrabold mt-0.5">{SCHOOL_NAME}</div>
                <div className="text-xs text-teal-50/90 mt-0.5">نظام إدارة الطلاب</div>
              </div>
            </div>
            <div className="gold-divider mt-5" />
          </div>

          {/* Form */}
          <form onSubmit={onSubmit} className="p-7 space-y-5" data-testid="login-form">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-teal-50 text-teal-700 mb-2">
                <ShieldCheck size={22} />
              </div>
              <h1 className="font-cairo text-xl font-bold text-slate-900">تسجيل الدخول</h1>
              <p className="text-xs text-slate-500 mt-1">أدخل بياناتك للدخول إلى لوحة التحكم</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="username">اسم المستخدم</Label>
              <div className="relative">
                <UserIcon size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input id="username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="admin" className="pr-9 h-11" dir="rtl" data-testid="login-username" autoComplete="username" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password">كلمة المرور</Label>
              <div className="relative">
                <KeyRound size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="pr-9 h-11" dir="rtl" data-testid="login-password" autoComplete="current-password" />
              </div>
            </div>

            <Button type="submit" disabled={loading} className="w-full h-11 bg-teal-700 hover:bg-teal-800 text-white gap-2 btn-elevate" data-testid="login-submit">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
              {loading ? "جاري الدخول..." : "دخول"}
            </Button>

            <div className="text-[11px] text-slate-500 text-center leading-relaxed">
              للاستفسار أو نسيان كلمة المرور، تواصل مع<br />
              <span className="font-cairo font-bold text-teal-700">الأستاذ محمد كامل</span>
              <a href="tel:0590939015" className="block mt-1 text-teal-700 hover:text-teal-800 tabular-nums font-bold" dir="ltr">0590939015</a>
            </div>
          </form>
        </div>

        <div className="text-center mt-4 text-[11px] text-slate-500">
          © {new Date().getFullYear()} {SCHOOL_NAME} — تصميم وبرمجة الأستاذ محمد كامل
        </div>
      </div>
    </div>
  );
}
